import React, { useCallback } from 'react';
import { ProductSwitcherLink, useLocation, useUrlBuilder } from '@app/router';
import PortfolioItemConstants from '@sell/const/PortfolioItem';
import AddPortfolioItemCTA from '@account/components/AddPortfolioItemCTA';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { track } from '@app/analytics';
import { trackEvent, EVENTS } from '@app/segment';
import { useCurrency } from '@app/currency';
import { LANGUAGES } from '@app/i18n/data/languages';
import { getLanguageCode } from '@app/redux/localization/selectors';
import {
  Heading,
  Text,
  ButtonGroup,
  Flex,
  Box,
  useBreakpointValue,
} from '@stockx/ui';
import { usePageType } from '@app/page';
import { PageType } from '@app/config';
import BidAskButton from './components/BidAskButton';
import {
  CalendarTileStyles,
  CalendarTileInnerStyles,
  CalendarTileNameStyles,
} from './CalenderTile.styles';
import CalendarTileImage from './components/CalenderTileImage';
import PriceLine from './components/PriceLine';
import ReleaseDate from './components/ReleaseDate';

export const CalendarTile = ({
  product,
  tracking,
  screenName,
  swooshName = '',
  position,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpointValue();
  const urlBuilder = useUrlBuilder();
  const { currency } = useCurrency();
  const { pathname } = useLocation();
  const langCode = useSelector(getLanguageCode) || 'en';
  const isVerbose = LANGUAGES[langCode]?.isVerbose;

  const dispatch = useDispatch();
  const trackAction = useCallback(
    (payload) => dispatch(track(payload)),
    [dispatch],
  );

  const trackClick = () => {
    const {
      market: { lowestAsk },
    } = product;

    trackEvent(EVENTS.PRODUCT_TILE, {
      swooshName,
      screenName,
      product,
      lowestAsk,
      position,
      currency,
      objectID: product.uuid,
    });

    if (tracking) {
      trackAction({
        event: tracking,
        properties: { label: product.title, category: product.productCategory },
      });
    }
  };

  const pageType = usePageType();
  const isHomepage = pageType === PageType.Home;
  const priorityCount = isMobile ? 3 : 4;

  let styleVariant;
  if (isHomepage) {
    styleVariant = 'home';
  } else if (pageType === PageType.Release2) {
    styleVariant = 'release';
  } else {
    styleVariant = 'browse';
  }

  return (
    <Box
      key={product.uuid}
      data-testid="calendar-tile"
      sx={CalendarTileStyles.variants[styleVariant]}
    >
      <Box sx={CalendarTileInnerStyles.variants[styleVariant]}>
        <Flex justify="space-between" minH={8} mb={1}>
          <div>
            <Heading
              variant="serif"
              as="span"
              size={isHomepage ? 'xl' : { base: 'xl', sm: '2xl' }}
            >
              {product?.releaseDate || product?.traits ? (
                <ReleaseDate product={product} />
              ) : (
                <span>{t('TBD')}</span>
              )}
            </Heading>
          </div>
          <Box mt={2}>
            <AddPortfolioItemCTA
              action={PortfolioItemConstants.actions.WANT}
              productId={product.uuid}
              product={product}
              icon
            />
          </Box>
        </Flex>
        <ProductSwitcherLink
          to={urlBuilder(product.urlKey)}
          onClick={trackClick}
          id={product.title}
        >
          <Box maxW={isHomepage ? '172px' : undefined}>
            <CalendarTileImage
              product={product}
              isHomepage={isHomepage}
              isPriority={!isHomepage && position < priorityCount}
            />
            {!isHomepage && <PriceLine {...product.market} />}
            <Box>
              <Text
                sx={CalendarTileNameStyles.variants[styleVariant]}
                fontSize={{ base: 'xs', sm: 'sm' }}
              >
                {product.title}
              </Text>
            </Box>
            {isHomepage && <PriceLine {...product.market} />}
          </Box>
        </ProductSwitcherLink>
        <ButtonGroup
          w="100%"
          flexDirection={isVerbose ? 'column' : 'row'}
          isAttached={isVerbose}
        >
          {screenName !== 'Home' && (
            <BidAskButton
              title={`${t('Ask')}`}
              productId={product.id}
              context={isVerbose ? 'buying' : 'selling'}
              urlKey={product?.urlKey}
              currentLocation={pathname}
              isVerboseLang={isVerbose}
            />
          )}
          <BidAskButton
            title={`${t('Bid')}`}
            productId={product.id}
            context={isVerbose ? 'selling' : 'buying'}
            urlKey={product?.urlKey}
            currentLocation={pathname}
            isVerboseLang={isVerbose}
          />
        </ButtonGroup>
      </Box>
    </Box>
  );
};

export default CalendarTile;
